import React from "react";
import { i18n } from "@/utils/i18n";
import { TableBody, TableHead, TableRow, Typography, CircularProgress } from "@mui/material";
import CommentField from "../fields/comment";
import StatusField from "../fields/status";
import { Styled } from "./styles";
import constant from "@/utils/constant";
import { FormField } from "../index";
import { formatNegativeAmount } from "@/utils/helpers";

interface Props {
    filteredData: FormField[];
    onDownload: (cid: string | number) => Promise<void>;
    isLoading: (string | number)[];
}

const { CURRENCIES } = constant;

const Outgoings: React.FC<Props> = ({ filteredData, onDownload, isLoading }) => {
    if (filteredData.length === 0) {
        return null;
    }

    return (
        <>
            <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                    borderBottom: "2px solid",
                    borderColor: "primary.main",
                    display: "inline-block",
                    paddingBottom: 2,
                    marginLeft: 2,
                    marginTop: 3,
                    marginBottom: 2,
                }}
            >
                {i18n.t("outgoings")}
            </Typography>

            {filteredData.map(item => {
                const amountNum = Number(item.amount);
                const taxAmountNum = Number(item.sum_tax_amount);
                return (
                    <Styled.Table size="small" key={item.fieldId}>
                        <TableHead>
                            <TableRow>
                                <Styled.HeaderCell>Ref.nr</Styled.HeaderCell>
                                <Styled.HeaderCell>{i18n.t("date")}</Styled.HeaderCell>
                                <Styled.HeaderKategoriCell>
                                    {i18n.t("category")} / {i18n.t("tag")}
                                </Styled.HeaderKategoriCell>
                                <Styled.HeaderCommentPlaceholderCell />
                                <Styled.HeaderCell>{i18n.t("verification")}</Styled.HeaderCell>
                                <Styled.HeaderCell>{i18n.t("taxDeduction")}</Styled.HeaderCell>
                                <Styled.HeaderCell>{i18n.t("amount")}</Styled.HeaderCell>
                                <Styled.HeaderCell>{i18n.t("status")}</Styled.HeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow key={`${item.id}-main`}>
                                <Styled.DataCell colSpan={2}></Styled.DataCell>
                                <Styled.BoldDataCell>{item.description}</Styled.BoldDataCell>
                                <Styled.DataCell>
                                    <CommentField variant="outgoings" index={item.originalIndex} />
                                </Styled.DataCell>
                                <Styled.DataCell>
                                    {item.has_attachments && (
                                        <Styled.ClickablePdfText
                                            variant="body2"
                                            color="primary"
                                            onClick={() => onDownload(item.id)}
                                            sx={{
                                                cursor: isLoading.includes(item.id)
                                                    ? "not-allowed"
                                                    : "pointer",
                                            }}
                                        >
                                            {isLoading.includes(item.id) ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                i18n.t("button.downloadCategoryPDF")
                                            )}
                                        </Styled.ClickablePdfText>
                                    )}
                                </Styled.DataCell>
                                <Styled.BoldDataCell>
                                    {CURRENCIES.KRONA.format(taxAmountNum)}
                                </Styled.BoldDataCell>
                                <Styled.BoldDataCell>
                                    {CURRENCIES.KRONA.format(amountNum)}
                                </Styled.BoldDataCell>
                                <Styled.DataCell>
                                    <StatusField variant="outgoings" index={item.originalIndex} />
                                </Styled.DataCell>
                            </TableRow>
                            {(item.verifications || []).map(verification => (
                                <TableRow key={verification.tid}>
                                    <Styled.DataCell>#{verification.tid}</Styled.DataCell>
                                    <Styled.DataCell>
                                        {Intl.DateTimeFormat("sv-SE").format(
                                            new Date(verification.transaction_date),
                                        )}
                                    </Styled.DataCell>
                                    <Styled.DataCell colSpan={2}>
                                        {verification.transaction_description}
                                        <Styled.Chip>{verification.tag_name}</Styled.Chip>
                                    </Styled.DataCell>
                                    <Styled.DataCell>-</Styled.DataCell>
                                    <Styled.DataCell>
                                        {CURRENCIES.KRONA.format(
                                            Number(verification.tax_amount || 0),
                                        )}
                                    </Styled.DataCell>
                                    <Styled.DataCell colSpan={2}>
                                        {CURRENCIES.KRONA.format(Number(verification.amount))}
                                    </Styled.DataCell>
                                </TableRow>
                            ))}
                            <TableRow key={`${item.id}-summary`}>
                                <Styled.DataCell colSpan={2}></Styled.DataCell>
                                <Styled.BoldDataCell colSpan={3}>
                                    {i18n.t("amount")} {item.description}
                                </Styled.BoldDataCell>
                                <Styled.BoldDataCell>
                                    {formatNegativeAmount(taxAmountNum)}
                                </Styled.BoldDataCell>
                                <Styled.BoldDataCell>
                                    {formatNegativeAmount(amountNum)}
                                </Styled.BoldDataCell>
                                <Styled.DataCell></Styled.DataCell>
                            </TableRow>
                        </TableBody>
                    </Styled.Table>
                );
            })}
        </>
    );
};

export default Outgoings;
